'use client';

import React from 'react';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Whether the textarea is invalid */
  invalid?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Resize behavior */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /** Font family override */
  fontFamily?: string;
  /** Font size override */
  fontSize?: string;
  /** Min height override */
  minHeight?: string;
  /** Color override */
  color?: string;
}

const sizeStyles: Record<string, React.CSSProperties> = {
  sm: {
    padding: '6px 10px',
    fontSize: '14px',
    minHeight: '60px',
  },
  md: {
    padding: '8px 12px',
    fontSize: '14px',
    minHeight: '80px',
  },
  lg: {
    padding: '10px 14px',
    fontSize: '16px',
    minHeight: '100px',
  },
};

/**
 * Textarea component for multi-line text input.
 * Uses native textarea element with inline styles to ensure consistent rendering
 * regardless of Chakra UI token resolution context.
 */
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      invalid = false,
      disabled = false,
      readOnly = false,
      required = false,
      size = 'md',
      resize = 'vertical',
      fontFamily: fontFamilyProp,
      fontSize: fontSizeProp,
      minHeight: minHeightProp,
      color: colorProp,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const sizeStyle = sizeStyles[size as keyof typeof sizeStyles];

    const baseStyle: React.CSSProperties = {
      display: 'block',
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: '8px',
      border: `1px solid ${invalid ? '#EF4444' : '#D1D5DB'}`,
      backgroundColor: disabled ? '#F9FAFB' : '#FFFFFF',
      color: '#111827',
      resize,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      outline: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.5',
      ...sizeStyle,
      ...(fontFamilyProp
        ? { fontFamily: fontFamilyProp === 'mono' ? 'monospace' : fontFamilyProp }
        : {}),
      ...(fontSizeProp
        ? {
            fontSize:
              fontSizeProp === 'xs' ? '12px' : fontSizeProp === 'sm' ? '14px' : fontSizeProp,
          }
        : {}),
      ...(minHeightProp ? { minHeight: minHeightProp } : {}),
      ...(colorProp ? { color: colorProp } : {}),
      ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
      ...style,
    };

    return (
      <textarea
        ref={ref}
        className={className}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        aria-invalid={invalid}
        style={baseStyle}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = invalid ? '#EF4444' : '#3B82F6';
          e.currentTarget.style.boxShadow = `0 0 0 1px ${invalid ? '#EF4444' : '#3B82F6'}`;
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = invalid ? '#EF4444' : '#D1D5DB';
          e.currentTarget.style.boxShadow = 'none';
          props.onBlur?.(e);
        }}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
